import { LayoutData, LayouterType } from './LayoutData';
import { BaseLayouter } from './layouters/BaseLayouter';
import { LayouterFactory } from './layouters/LayouterFactory';
import { POI } from '../poi/POI';
import { Logger } from '../core/Logger';
import { UUID } from '../types';
import { Vector3 } from '../types/Vector3';
import { Color } from '../types/Color';
import * as THREE from 'three';
import { ProcessCurve } from '../navigation/ProcessCurve';
import { MorphableCurve } from '../curves/MorphableCurve';

export interface LayoutSettings {
    preserveExplicitPositions: boolean;
    animateTransitions: boolean;
    transitionDuration: number;
}

export class Layout {
    public readonly id: UUID;
    public name: string;
    public layoutData: LayoutData;
    public settings: LayoutSettings;
    
    private _layouter: BaseLayouter | null = null;
    private _debugVisualization: THREE.Group | null = null;
    private _currentScene: any = null;
    private _processCurve: ProcessCurve | null = null;
    private _morphableCurve: MorphableCurve | null = null;
    
    constructor(
        name: string = 'Layout',
        layoutData?: LayoutData,
        settings?: Partial<LayoutSettings>
    ) {
        this.id = crypto.randomUUID();
        this.name = name;
        this.layoutData = layoutData || new LayoutData();
        this.settings = {
            preserveExplicitPositions: true,
            animateTransitions: false,
            transitionDuration: 1000,
            ...settings
        };
        
        // Initialize layouter
        this.updateLayouter();
    }
    
    // Layout management
    setLayoutData(layoutData: LayoutData): void {
        this.layoutData = layoutData;
        this.updateLayouter();
        this.updateDebugVisualization();
    }
    
    private updateLayouter(): void {
        //console.log(`🏭 LAYOUT DEBUG: updateLayouter() called`);
        //console.log(`🏭 LAYOUT DEBUG: this.layoutData.layouter = '${this.layoutData.layouter}'`);
        
        this._layouter = LayouterFactory.create(this.layoutData.layouter);
        //console.log(`🏭 LAYOUT DEBUG: LayouterFactory.create('${this.layoutData.layouter}') returned:`, this._layouter);
        
        if (!this._layouter) {
            console.warn(`🏭 LAYOUT WARNING: Unknown layouter: ${this.layoutData.layouter}, falling back to grid`);
            this._layouter = LayouterFactory.createGrid();
            this.layoutData.layouter = 'grid';
            //console.log(`🏭 LAYOUT DEBUG: Fallback to grid layouter:`, this._layouter);
        } else {
            //console.log(`🏭 LAYOUT SUCCESS: Created layouter '${this._layouter.name}' successfully`);
        }
    }
    
    // ✅ Apply layout to provided POI list (reference-based)
    applyLayout(poisList: POI[]): void {
        //console.log(`🎯 LAYOUT DEBUG: applyLayout() called with ${poisList.length} POIs`);
        //console.log(`🎯 LAYOUT DEBUG: Current layouter:`, this._layouter ? this._layouter.name : 'null');
        
        if (!this._layouter || poisList.length === 0) {
            //console.log(`🎯 LAYOUT DEBUG: Early return - layouter=${!!this._layouter}, poisList.length=${poisList.length}`);
            return;
        }
        
        // Validate layout parameters
        if (!this._layouter.validateParameters(this.layoutData)) {
            console.warn(`🎯 LAYOUT WARNING: Invalid parameters for layouter: ${this.layoutData.layouter}`);
            return;
        }
        
        //console.log(`🎯 LAYOUT DEBUG: Validation passed, applying layout with '${this._layouter.name}' layouter`);
        
        // ✅ Reset POI shapes to default if flag is enabled
        if (this.layoutData.resetShapesToDefault.value) {
            //console.log(`🔄 LAYOUT DEBUG: Resetting ${poisList.length} POI shapes to default`);
            this.resetPOIShapesToDefault(poisList);
        }
        
        // Check if layouter has the cleaner applyToObjects method
        if ((this._layouter as any).applyToObjects) {
            //console.log(`🎯 LAYOUT DEBUG: Using modern applyToObjects method`);
            // Modern approach: let the layouter handle everything directly (single iteration)
            (this._layouter as any).applyToObjects(poisList, this.layoutData);

            // ✨ MODERN LAYOUTERS: Use the newly assigned POI target positions directly
            // Read from poidata.position.value (these were set synchronously by applyToObjects)
            const newPositions: Vector3[] = poisList
                .map(poi => poi.poidata?.position?.value)
                .filter(pos => !!pos) as Vector3[];

            //console.log(`✨ LAYOUT DEBUG: Updating MorphableCurve for modern layouter with ${newPositions.length} positions`);
            const showMorphline = (poisList[0]?.getSceneSettings?.()?.showMorphline?.value) ?? true;
            if (showMorphline) {
                if (newPositions.length >= 2) {
                    this.updateMorphableCurveWithPositions(newPositions);
                } else {
                    // Fallback to reading current transforms if something went wrong
                    this.updateMorphableCurve(poisList);
                }
            } else if (this._morphableCurve) {
                this._morphableCurve.setVisible(false);
            }
        } else {
            //console.log(`🎯 LAYOUT DEBUG: Using fallback calculate* methods`);
            // Fallback: use the old calculate* methods (single iteration)
            poisList.forEach((poi, index) => {
                const position = this._layouter!.calculatePosition(index, poisList.length, this.layoutData);
                const orientation = this._layouter!.calculateOrientation(index, poisList.length, this.layoutData);
                
                //console.log(`🎯 LAYOUT DEBUG: POI ${index} - position: ${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}`);
                
                // Apply position and orientation to POI
                this.applyTransformToPOI(poi, position, orientation);
            });
            
            // ✨ OLD LAYOUTERS: Update MorphableCurve immediately after all POI positions are set
            //console.log(`✨ LAYOUT DEBUG: About to update MorphableCurve with ${poisList.length} POIs`);
            this.updateMorphableCurveForOldLayouters(poisList);
        }
        
        //console.log(`🎯 LAYOUT DEBUG: Layout application complete`);
        
        // Update debug visualization after applying layout WITH REAL OBJECT COUNT
        this.updateDebugVisualizationWithObjectCount(poisList.length);
        
        // Note: Process curve update will be handled by setLayouter method to ensure proper timing
    }
    
    // Apply transform to a specific POI (proper implementation)
    private applyTransformToPOI(poi: any, position: any, orientation: any): void {
        //console.log(`🎯 APPLYING TRANSFORM TO POI ${poi.name}:`);
        //console.log(`  Position: ${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}`);
        //console.log(`  Orientation: ${orientation.x.toFixed(2)}°, ${orientation.y.toFixed(2)}°, ${orientation.z.toFixed(2)}°`);
        
        // 🐛 DEBUG: Log scale before any changes
        const scaleBeforeLayout = poi.poidata.scale.value ?? 1.0;
        //console.log(`  🔍 Scale BEFORE layout: ${scaleBeforeLayout.toFixed(4)}`);
        
        // Apply transformations directly to POI data
        poi.poidata.position.value = position;
        poi.poidata.rotation.value = orientation;
        
        // Apply scale if layouter provides uniform scale
        if (this._layouter?.calculateScale) {
            const layoutScale = this._layouter.calculateScale(0, 1, this.layoutData); // Use dummy values since scale is usually uniform
            const originalScale = poi.poidata.scale.value ?? 1.0; // Preserve original scale from POIData
            const combinedScale = originalScale * layoutScale; // Combine scales multiplicatively
            poi.poidata.scale.value = combinedScale;
            //console.log(`  📏 Scale: layout=${layoutScale.toFixed(2)} × original=${originalScale.toFixed(2)} = ${combinedScale.toFixed(2)}`);
        } else {
            //console.log(`  📏 Scale UNCHANGED (no calculateScale): ${scaleBeforeLayout.toFixed(4)}`);
        }
        
        // 🐛 DEBUG: Log final scale after layout
        const scaleAfterLayout = poi.poidata.scale.value ?? 1.0;
        //console.log(`  🔍 Scale AFTER layout: ${scaleAfterLayout.toFixed(4)}`);
    }
    
    // ✅ Apply layout to specific POI by index (reference-based)
    applyLayoutToPOI(poisList: POI[], index: number): void {
        if (!this._layouter || index < 0 || index >= poisList.length) {
            return;
        }
        
        const poi = poisList[index];
        const position = this._layouter.calculatePosition(index, poisList.length, this.layoutData);
        const orientation = this._layouter.calculateOrientation(index, poisList.length, this.layoutData);
        
        this.applyTransformToPOI(poi, position, orientation);
    }
    
    // ✅ Get layout preview (positions without applying) - reference-based
    getLayoutPreview(poisList: POI[]): Array<{ poi: POI; position: any; orientation: any }> {
        if (!this._layouter) {
            return [];
        }
        
        return poisList.map((poi, index) => ({
            poi,
            position: this._layouter!.calculatePosition(index, poisList.length, this.layoutData),
            orientation: this._layouter!.calculateOrientation(index, poisList.length, this.layoutData)
        }));
    }
    
    // ✅ Layout analysis - reference-based
    getLayoutBounds(poisList: POI[]): { min: any; max: any } | null {
        const preview = this.getLayoutPreview(poisList);
        if (preview.length === 0) {
            return null;
        }
        
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        
        preview.forEach(({ position }) => {
            minX = Math.min(minX, position.x);
            minY = Math.min(minY, position.y);
            minZ = Math.min(minZ, position.z);
            maxX = Math.max(maxX, position.x);
            maxY = Math.max(maxY, position.y);
            maxZ = Math.max(maxZ, position.z);
        });
        
        return {
            min: { x: minX, y: minY, z: minZ },
            max: { x: maxX, y: maxY, z: maxZ }
        };
    }
    
    /**
     * Update debug visualization for the current layouter
     */
    public updateDebugVisualization(scene?: any): void {
        if (scene) {
            this._currentScene = scene;
        }
        
        if (!this._layouter || !this._currentScene) {
            return;
        }

        // Remove old debug visualization
        if (this._debugVisualization && this._currentScene.remove) {
            this._currentScene.remove(this._debugVisualization);
            this._debugVisualization = null;
        }
        // Debug visualization disabled
    }

    /**
     * Update debug visualization with real object count
     */
    public updateDebugVisualizationWithObjectCount(objectCount: number, scene?: any): void {
        if (scene) {
            this._currentScene = scene;
        }
        
        if (!this._layouter || !this._currentScene) {
            return;
        }

        // Remove old debug visualization
        if (this._debugVisualization && this._currentScene.remove) {
            this._currentScene.remove(this._debugVisualization);
            this._debugVisualization = null;
        }
        // Debug visualization disabled
    }
    
    /**
     * Convert layouter string to LayouterType enum
     */
    private getLayouterTypeEnum(layouterString: string): LayouterType {
        switch (layouterString) {
            case 'grid': return LayouterType.Grid;
            case 'circle': 
            case 'circle_outside': 
            case 'circle_inside': return LayouterType.Circle;
            case 'spiral': return LayouterType.Spiral;
            case 'line':
            case 'line2':
            case 'uphill':
            case 'uphill_rotated': 
            case 'horizontal':
            case 'vertical': return LayouterType.Line;
            case 'hexagonal': return LayouterType.Hexagonal;
            case 'spherical_hexagonal': return LayouterType.SphericalHexagonal;
            case 'modern-line': return LayouterType.ModernLine;
            case 'modern-circle': return LayouterType.ModernCircle;
            case 'modern-spiral': return LayouterType.ModernSpiral;
            case 'modern-line-adaptive': return LayouterType.ModernLineAdaptive;
            case 'modern-circle-adaptive': return LayouterType.ModernCircleAdaptive;
            case 'modern-spiral-adaptive': return LayouterType.ModernSpiralAdaptive;
            case 'modern-sphere-grid': return LayouterType.ModernSphereGrid;
            case 'modern-sphere-hex': return LayouterType.ModernSphereHex;
            case 'modern-plane-grid': return LayouterType.ModernPlaneGrid;
            case 'modern-sphere-hex-rings': return LayouterType.ModernSphereHexRings;
            case 'modern-plane-hex-rings': return LayouterType.ModernPlaneHexRings;
            case 'modern-sphere-hex-honeycomb': return LayouterType.ModernSphereHexHoneycomb;
            default:
                console.warn(`🛤️ Unknown layouter type: ${layouterString}, defaulting to Line`);
                return LayouterType.Line;
        }
    }
    
    /**
     * Update process curve specifically for old layouters (immediate update)
     */
    private updateMorphableCurveForOldLayouters(poisList: POI[]): void {
        // Respect showMorphline scene-setting before doing any curve work.
        const showMorphline = (poisList[0]?.getSceneSettings?.()?.showMorphline?.value) ?? true;
        if (!showMorphline) {
            if (this._morphableCurve) this._morphableCurve.setVisible(false);
            return;
        }

        // Get the NEW target positions that were just calculated by the layouter
        const newPositions: Vector3[] = [];
        poisList.forEach((poi, index) => {
            const position = this._layouter!.calculatePosition(index, poisList.length, this.layoutData);
            newPositions.push(position);
        });

        this.updateMorphableCurveWithPositions(newPositions);
    }
    
    /**
     * Update morphable curve with calculated positions (avoids timing issues)
     */
    /**
     * Verschiebt Kurvenpunkte horizontal-radial vom Zentroid weg (curveRadialOffset).
     * So läuft die Linie als Band hinter/vor den Objekten statt durch sie hindurch —
     * bei einer Spirale ergibt positiver Offset das äußere Band.
     */
    private offsetCurvePositions(positions: Vector3[]): Vector3[] {
        const radial = this.layoutData?.curveRadialOffset?.value ?? 0;
        const vertical = this.layoutData?.curveVerticalOffset?.value ?? 0;
        if ((!radial && !vertical) || positions.length < 2) return positions;
        let cx = 0, cz = 0;
        for (const p of positions) { cx += p.x; cz += p.z; }
        cx /= positions.length; cz /= positions.length;
        return positions.map(p => {
            const dx = p.x - cx, dz = p.z - cz;
            const len = Math.sqrt(dx * dx + dz * dz);
            // Punkt auf der Achse: radiale Richtung unbestimmt, nur vertikal versetzen.
            if (len < 1e-6) return new Vector3(p.x, p.y + vertical, p.z);
            return new Vector3(p.x + (dx / len) * radial, p.y + vertical, p.z + (dz / len) * radial);
        });
    }

    /**
     * Sphaerische Layouts: Kurvenpunkte auf die Kugeloberflaeche projizieren.
     * CatmullRom verbindet Stationen sonst durch den RAUM — die Sehnen stechen
     * durchs Kugelinnere und kreuzen als Gitter die ganze Szene. Center/Radius
     * kommen aus den Stationspositionen selbst; curveRadialOffset verschiebt
     * das Band relativ zur Oberflaeche (negativ = innen, hinter den Karten).
     */
    private configureCurvePostProcessing(positions: Vector3[]): void {
        if (!this._morphableCurve) return;
        const isSpherical = String(this.layoutData?.layouter ?? '').includes('spher');
        if (!isSpherical || positions.length < 3) {
            this._morphableCurve.setPathPostProcessor(null);
            return;
        }
        const c = new Vector3(0, 0, 0);
        for (const p of positions) { c.x += p.x; c.y += p.y; c.z += p.z; }
        c.x /= positions.length; c.y /= positions.length; c.z /= positions.length;
        let radius = 0;
        for (const p of positions) {
            const dx = p.x - c.x, dy = p.y - c.y, dz = p.z - c.z;
            radius += Math.sqrt(dx * dx + dy * dy + dz * dz);
        }
        radius = radius / positions.length + (this.layoutData?.curveRadialOffset?.value ?? 0);
        this._morphableCurve.setPathPostProcessor(pts => pts.map(p => {
            const dx = p.x - c.x, dy = p.y - c.y, dz = p.z - c.z;
            const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
            if (len < 1e-6) return p;
            const f = radius / len;
            return new Vector3(c.x + dx * f, c.y + dy * f, c.z + dz * f);
        }));
    }

    private updateMorphableCurveWithPositions(positions: Vector3[]): void {
        if (!this._morphableCurve) {
            this._morphableCurve = new MorphableCurve(512); // 64 war zu wenig: bei 100+ Stationen blieb weniger als ein Stuetzpunkt pro Station — die Linie wurde zum Polygonzug (Befund Alex #119655)
            if (this._currentScene) {
                this._morphableCurve.setScene(this._currentScene);
            }
            //console.log(`✨ Created new MorphableCurve with 64 vertices - always visible!`);
        }
        
        if (positions.length < 2) {
            //console.log(`⚠️ MorphableCurve: Need at least 2 positions, got ${positions.length}`);
            return;
        }
        
        // Set default curve style (can be customized later)
        this._morphableCurve.setStyle({
            thickness: 0.02, // ← Jetzt in Pixel-Einheiten (0.02 * 100 = 2 Pixel)
            color: new Color('#fff5ee'), // Creme wie die Stationen — Blau war ein Debug-Rest
            opacity: 1.0,
            material: 'basic'
        });
        
        // Morph to new path - this is where the magic happens! ✨
        const animationDuration = 1.0; // Seconds
        this.configureCurvePostProcessing(positions);
        // Bei sphaerischer Projektion uebernimmt der PostProcessor den Versatz —
        // der horizontale Zentroid-Offset wuerde die Kugelgeometrie nur verzerren.
        const isSpherical = String(this.layoutData?.layouter ?? '').includes('spher');
        const pathPoints = isSpherical ? positions : this.offsetCurvePositions(positions);
        this._morphableCurve.morphToPath(pathPoints, animationDuration);
        
        // Always visible!
        this._morphableCurve.setVisible(true);
    }
    
    /**
     * Update morphable curve (fallback - reads current POI positions)
     */
    private updateMorphableCurve(poisList: POI[]): void {
        if (!this._morphableCurve) {
            this._morphableCurve = new MorphableCurve(512); // 64 war zu wenig: bei 100+ Stationen blieb weniger als ein Stuetzpunkt pro Station — die Linie wurde zum Polygonzug (Befund Alex #119655)
            if (this._currentScene) {
                this._morphableCurve.setScene(this._currentScene);
            }
            //console.log(`✨ Created new MorphableCurve with 64 vertices - always visible!`);
        }
        
        if (poisList.length < 2) {
            //console.log(`⚠️ MorphableCurve: Need at least 2 POIs, got ${poisList.length}`);
            return;
        }
        
        // Get POI positions
        const positions = poisList
            .map(poi => poi.transform.position.value || poi.poidata.position.value)
            .filter(pos => pos !== null) as Vector3[];
            
        if (positions.length >= 2) {
            // Set default curve style (can be customized later)
            this._morphableCurve.setStyle({
                thickness: 0.15,
                color: new Color('#fff5ee'), // Creme wie die Stationen — Blau war ein Debug-Rest
                opacity: 1.0,
                material: 'basic'
            });
            
            // Morph to new path - this is where the magic happens! ✨
            const animationDuration = 1.0; // Seconds
            this.configureCurvePostProcessing(positions);
            const isSpherical2 = String(this.layoutData?.layouter ?? '').includes('spher');
            this._morphableCurve.morphToPath(isSpherical2 ? positions : this.offsetCurvePositions(positions), animationDuration);
            
            // Always visible!
            this._morphableCurve.setVisible(true);
        }
    }
    
    /**
     * Update process curve visualization (legacy system)
     */
    public updateProcessCurve(poisList: POI[], sceneSettings?: any): void {
        if (!this._processCurve) {
            this._processCurve = new ProcessCurve();
            if (this._currentScene) {
                this._processCurve.setScene(this._currentScene);
            }
        }
        
        // Check if process curve should be visible
        const showCurve = sceneSettings?.showProcessCurve?.value ?? false;
        
        if (!showCurve || poisList.length < 2 || !this._layouter) {
            this._processCurve.hide();
            return;
        }
        
        // Get POI positions - filter out null values
        const positions = poisList
            .map(poi => poi.transform.position.value || poi.poidata.position.value)
            .filter(pos => pos !== null) as Vector3[];
        
        if (positions.length >= 2) {
            // Update styling from settings
            if (sceneSettings) {
                this._processCurve.updateStyle({
                    curveColor: sceneSettings.processCurveColor?.value,
                    curveThickness: sceneSettings.processCurveThickness?.value,
                    markerSize: sceneSettings.processCurveMarkerSize?.value,
                    numberColor: sceneSettings.processNumberColor?.value,

                    showMarkerSpheres: sceneSettings.showProcessMarkerSpheres?.value,
                    fontSize: sceneSettings.processNumberFontSize?.value,
                    fontFamily: sceneSettings.processNumberFontFamily?.value,
                    fontWeight: sceneSettings.processNumberFontWeight?.value,
                    lineOffset: sceneSettings.processNumberLineOffset?.value || 0,
                    billboardScale: sceneSettings.processBillboardScale?.value || 1.0,
                    billboardBackgroundColor: sceneSettings.processBillboardBackgroundColor?.value,
                    billboardBorderColor: sceneSettings.processBillboardBorderColor?.value,
                    showStartMarker: sceneSettings.showProcessStartMarker?.value,
                    startMarkerText: sceneSettings.processStartMarkerText?.value
                });
            }
            
            // Update curve with animation (convert string to LayouterType enum)
            const layouterType = this.getLayouterTypeEnum(this.layoutData.layouter);
            //console.log(`🛤️ LAYOUT DEBUG: Converting layouter '${this.layoutData.layouter}' to enum '${layouterType}'`);
            this._processCurve.updateCurve(positions, layouterType, true);
            
            // Show the curve
            this._processCurve.setVisible(true);
            
            //Logger.Instance.Debug(`🛤️ Updated process curve for ${positions.length} POIs with ${this.layoutData.layouter} layout`, 'Layout');
        } else {
            this._processCurve.hide();
        }
    }
    
    /**
     * Update ProcessCurve animation (call from main update loop)
     */
    public updateProcessCurveAnimation(deltaTime: number): void {
        if (this._processCurve) {
            this._processCurve.update(deltaTime);
        }
    }

    /**
     * Set the scene for debug visualization
     */
    public setScene(scene: any): void {
        this._currentScene = scene;
        
        // Initialize MorphableCurve with scene if it exists
        if (this._morphableCurve) {
            this._morphableCurve.setScene(scene);
        }
        
        this.updateDebugVisualization();
    }
    
    // Validation
    validate(): boolean {
        // Validate layout data
        if (!this.layoutData.validate()) {
            return false;
        }
        
        // Validate layouter
        if (!this._layouter) {
            return false;
        }
        
        // Validate layouter parameters
        if (!this._layouter.validateParameters(this.layoutData)) {
            return false;
        }
        
        return true;
    }
    
    /**
     * Reset POI shapes to their default values from SceneSettings
     */
    private resetPOIShapesToDefault(poisList: POI[]): void {
        poisList.forEach((poi, index) => {
            try {
                // Reset shape to default from SceneSettings if available
                const sceneSettings = poi.getSceneSettings();
                if (poi.poidata && poi.poidata.shape && sceneSettings?.shape) {
                    const defaultShape = sceneSettings.shape.value;
                    if (defaultShape && defaultShape !== poi.poidata.shape.value) {
                        //console.log(`🔄 LAYOUT: Resetting POI ${index} shape from '${poi.poidata.shape.value}' to default '${defaultShape}'`);
                        
                        // Setting the value will automatically trigger the shape change handler
                        // which will recreate the mesh via the POI's internal onShapeDataChanged mechanism
                        poi.poidata.shape.value = defaultShape;
                    }
                }
            } catch (error) {
                console.warn(`Failed to reset shape for POI ${index}:`, error);
            }
        });
    }
    
    // Serialization
    serialize(): any {
        return {
            id: this.id,
            name: this.name,
            layoutData: this.layoutData.serialize(),
            settings: this.settings
        };
    }
    
    static deserialize(data: any): Layout {
        const layout = new Layout(data.name, LayoutData.create(data.layoutData), data.settings);
        // Note: Cannot modify readonly id property - it's auto-generated
        return layout;
    }
} 